// src/pages/ComponenteTareas.js
import React, { useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useFuncion } from "../context/FuncionContext";
import ReactDOM from "react-dom";
import { useAuth } from "../context/AuthContext";
import ProgressRing from "../components/ui/ProgressRing";
import cacheManager from "../utils/cacheManager";
import GamificationStats from "../components/gamification/GamificationStats";
import LoadingSpinner from "../components/LoadingSpinner";
import { getEcuadorYMD } from "../utils/ecuadorTime";
import {
  ArrowLeft,
  Package,
  BadgeCheck,
  ClipboardList,
  Clock,
  HelpCircle,
  Gamepad2,
  Info,
  ChevronRight,
  Heart,
  FolderOpen,
  Lock,
  Filter,
  X,
  Zap,
} from "lucide-react";
import TareasModal, { StartActivityButton } from "../components/tareas/tareasmodal";
import StreakModal from "../components/gamification/StreakModal";

/* ===== Utils para Tutorial ===== */
const decodeJwtPayload = (token) => {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/* =========================================================
   Modales de confirmación del tutorial
========================================================= */
function TourConfirmModal({ open, onConfirm, onCancel, tutorialKey }) {
  if (!open) return null;

  const node = (
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Confirmación">
      <div
        className="tour-dim"
        style={{
          left: 0,
          top: 0,
          width: "100vw",
          height: "100vh"
        }}
        onClick={onCancel}
      />

      <div
        className="tour-card tour-card-centered"  // ✅ AGREGAR CLASE
        style={{
          position: 'fixed',           // ✅ FORZAR fixed
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(520px, 92vw)',  // ✅ Ancho máximo
          maxWidth: '520px'            // ✅ Seguridad
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-head">
          <strong className="tour-title">¿Estás seguro de omitir estas indicaciones?</strong>
        </div>

        <div className="tour-text">
          <p className="tour-p">
            El tutorial te ayuda a familiarizarte con la plataforma y aprovechar
            todas sus funcionalidades para mejorar tu experiencia de aprendizaje.
          </p>
        </div>

        <div className="tour-actions">
          <button className="tour-btn ghost" onClick={onCancel} type="button">
            Cancelar
          </button>
          <button className="tour-btn primary" onClick={onConfirm} type="button">
            Sí, omitir
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}
function TourInfoModal({ open, onClose }) {
  if (!open) return null;

  const node = (
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Información">
      <div
        className="tour-dim"
        style={{
          left: 0,
          top: 0,
          width: "100vw",
          height: "100vh"
        }}
        onClick={onClose}
      />

      <div
        className="tour-card tour-card-centered"  // ✅ AGREGAR CLASE
        style={{
          position: 'fixed',           // ✅ FORZAR fixed
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(520px, 92vw)',  // ✅ Ancho máximo
          maxWidth: '520px'            // ✅ Seguridad
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-head">
          <strong className="tour-title">📚 Ayuda disponible</strong>
        </div>

        <div className="tour-text">
          <p className="tour-p">
            Toda esta información está disponible en el apartado de <strong>Ayuda</strong>.
          </p>
          <p className="tour-p">
            Podrás consultarla en cualquier momento para resolver dudas sobre el uso
            de la plataforma.
          </p>
        </div>

        <div className="tour-actions">
          <button className="tour-btn primary" onClick={onClose} type="button">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

const getUserKeyFromStorage = () => {
  try {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("accessToken");

    const payload = decodeJwtPayload(token);
    return (
      payload?.sub ||
      payload?.id ||
      payload?.user_id ||
      payload?.email ||
      payload?.codigo_institucional ||
      "anon"
    );
  } catch {
    return "anon";
  }
};

/* ===== Utils / Animación ===== */
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// ✅ Helper: interpretar banderas tipo secuencia_hijos / secuencial_hijos
const flagToBoolean = (value, defaultValue = true) => {
  if (value === undefined || value === null) return !!defaultValue;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (["true", "1", "yes", "si", "sí", "on", "t"].includes(v)) return true;
    if (["false", "0", "no", "off", "f"].includes(v)) return false;
  }
  return !!defaultValue;
};

const getFirstComponentIdFromStructure = (structure = []) => {
  const stack = Array.isArray(structure) ? [...structure] : [];
  while (stack.length) {
    const node = stack.shift();
    if (!node) continue;
    if (node.tipo === "componente") return node.id ?? null;
    if (Array.isArray(node.children) && node.children.length) {
      stack.unshift(...node.children);
    }
  }
  return null;
};

const collectNodesByType = (nodes, type) => {
  const results = [];
  const stack = Array.isArray(nodes) ? [...nodes] : [];
  while (stack.length) {
    const node = stack.shift();
    if (!node) continue;
    if (node.tipo === type) results.push(node);
    if (Array.isArray(node.children) && node.children.length) {
      stack.unshift(...node.children);
    }
  }
  return results;
};

/* =========================================================
   TOUR OVERLAY - Tutorial Interactivo
========================================================= */
function TourOverlay({
  open,
  step,
  steps,
  targetEl,
  onNext,
  onBack,
  onClose,
  showInfo,
  setShowInfo,
  tutorialKey,
}) {
  const [showConfirm, setShowConfirm] = React.useState(false);
  // Bloqueo de scroll cuando está abierto
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;

    const prevent = (e) => e.preventDefault();
    window.addEventListener("wheel", prevent, { passive: false });
    window.addEventListener("touchmove", prevent, { passive: false });

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("wheel", prevent);
      window.removeEventListener("touchmove", prevent);
    };
  }, [open]);

  const cardRef = useRef(null);
  const [cardSize, setCardSize] = useState({ w: 560, h: 220 });


  useLayoutEffect(() => {
    if (!open || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    if (r.width && r.height) setCardSize({ w: r.width, h: r.height });
  }, [step, open]);

  // Medir target
  const rect = targetEl?.getBoundingClientRect?.();
  const hasRect =
    rect &&
    Number.isFinite(rect.top) &&
    Number.isFinite(rect.left) &&
    rect.width > 0 &&
    rect.height > 0;

  const pad = 14;
  const margin = 12;
  const currentStepKey = steps?.[step]?.key;
  const currentStep = steps?.[step];

  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });

  useEffect(() => {
    if (!open) return;
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [open]);


  // ✅ Definir qué pasos son rectangulares
  const isRectangularStep = [
    "hero",
    "first_task_preview",
    "lives_system",
    "task_rewards",
    "coins_store",
    "comodines_detail",
  ].includes(currentStepKey);

  // ✅ Definir qué pasos son circulares
  const isCircularStep = [].includes(currentStepKey);
  const hole = hasRect
    ? (() => {
      if (isCircularStep) {
        // Expansión extra para comodines_detail: incluir el botón + morado
        const extraRight = currentStepKey === "comodines_detail" ? 50 : 0;
        const extraScale = currentStepKey === "comodines_detail" ? 1.3 : 1;

        const w0 = (rect.width + extraRight) * extraScale + pad * 1;
        const h0 = rect.height * extraScale + pad * 1;
        const d = Math.max(w0, h0, 150);

        // Centro desplazado a la derecha cuando hay expansión
        const cx = rect.left + (rect.width + extraRight) / 2;
        const cy = rect.top + rect.height / 2 + 10;

        const left = Math.max(margin, Math.min(cx - d / 2, window.innerWidth - d - margin));
        const top = Math.max(margin, Math.min(cy - d / 2, window.innerHeight - d - margin));
        return { top, left, width: d, height: d, cx, cy, isRect: false };
      } else if (isRectangularStep) {

        // ✅ ENFOQUE RECTANGULAR 100% basado en el rect real (RESPONSIVE)
        let top = rect.top - pad;
        let left = rect.left - pad;
        const width = rect.width + pad * 2;
        const height = rect.height + pad * 2;

        top = Math.max(margin, Math.min(top, vp.h - height - margin));
        left = Math.max(margin, Math.min(left, vp.w - width - margin));

        return {
          top,
          left,
          width,
          height,
          cx: left + width / 2,
          cy: top + height / 2,
          isRect: true,
        };
      }
      else {
        // ✅ DEFAULT: circular
        const w0 = rect.width + pad * 2;
        const h0 = rect.height + pad * 2;
        const d = Math.max(w0, h0, 120);
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const left = Math.max(margin, Math.min(cx - d / 2, window.innerWidth - d - margin));
        const top = Math.max(margin, Math.min(cy - d / 2, window.innerHeight - d - margin));
        return { top, left, width: d, height: d, cx, cy, isRect: false };
      }
    })()
    : null;

  const cardStyle = useMemo(() => {
    if (!hole) return {};

    const gap = 28;
    const marginSide = 16;
    const topbarBottom =
      document.querySelector(".task-topbar")?.getBoundingClientRect?.().bottom ?? 0;
    const safeTop = Math.max(12, topbarBottom + 12);

    const safeBottom = 18;

    const vw = vp.w;
    const vh = vp.h;

    const cw = cardSize.w || 560;
    const ch = cardSize.h || 220;

    // Centrado horizontal por defecto
    let left = hole.left + hole.width / 2 - cw / 2;
    left = Math.max(marginSide, Math.min(left, vw - cw - marginSide));

    const canBottom = hole.top + hole.height + gap + ch <= vh - safeBottom;
    const canTop = hole.top - gap - ch >= safeTop;

    let top;
    if (canBottom || !canTop) top = hole.top + hole.height + gap;
    else top = hole.top - gap - ch;

    top = Math.max(safeTop, Math.min(top, vh - ch - safeBottom));

    return { left, top };
  }, [hole, cardSize.w, cardSize.h]);

  // ✅ Bloquear clicks EXCEPTO en el último paso (start_task) donde solo se permite en el botón
  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      // Siempre permitir clicks en la tarjeta del modal
      if (e.target.closest(".tour-card")) return;

      const isLastStep = currentStep?.key === "start_task";

      if (isLastStep) {
        // En el último paso: solo permitir click en el botón de iniciar
        const isStartBtn = e.target.closest("[data-tour='start-btn']");
        if (!isStartBtn) {
          e.preventDefault();
          e.stopPropagation();
        }
      } else {
        // En todos los demás pasos: bloquear todo
        if (currentStep?.blockClicks || currentStep?.allowClick === undefined) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    document.addEventListener("click", handler, true);
    document.addEventListener("mousedown", handler, true);

    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("mousedown", handler, true);
    };
  }, [open, currentStep]);

  if (!open) return null;

  const s = steps?.[step] || {};
  const isFirst = step === 0;
  const isLast = step === (steps?.length || 1) - 1;

  // ✅ Componente de vidas para mostrar en el modal
  const LivesDemo = () => (
    <div className="tour-lives-demo">
      <div className="lives-display">
        <span className="q-heart">
          <Heart size={14} fill="currentColor" />
        </span>
        <span className="q-heart">
          <Heart size={14} fill="currentColor" />
        </span>
        <span className="q-heart off">
          <Heart size={14} />
        </span>
      </div>
      <p className="lives-caption">Ejemplo: 2 vidas disponibles de 3</p>
    </div>
  );

  const node = (
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Tutorial">
      {hole ? (
        <div
          className="tour-hole"
          style={{
            left: hole.left,
            top: hole.top,
            width: hole.width,
            height: hole.height,
            borderRadius: hole.isRect ? "18px" : "999px",
          }}
          aria-hidden="true"
        />
      ) : (
        <div className="tour-dim" style={{ left: 0, top: 0, width: "100vw", height: "100vh" }} />
      )}

      <div
        ref={cardRef}
        className={`tour-card ${hole ? "has-target" : ""} ${s.centered ? "tour-card-centered" : ""} ${s.customClass || ""}`}
        style={hole ? cardStyle : undefined}
        onClick={(e) => e.stopPropagation()}
      >

        <div className="tour-head">
          <strong className="tour-title">{s.title || "Tutorial"}</strong>
          <span className="tour-step">
            {step + 1}/{steps.length}
          </span>
        </div>

        {s.text ? (
          <div className="tour-text">
            {String(s.text)
              .split(/\n\n/g)
              .filter(Boolean)
              .map((p, i) => (
                <p key={i} className="tour-p">
                  {p.trim()}
                </p>
              ))}
          </div>
        ) : null}

        {/* ✅ Mostrar demo de vidas en el paso "lives_system" */}
        {s.key === "lives_system" && <LivesDemo />}

        <div className="tour-actions">
          {/* ✅ Botón OMITIR solo en el primer paso */}
          {isFirst && (
            <button
              className="tour-btn ghost"
              onClick={() => setShowConfirm(true)}
              type="button"
            >
              Omitir
            </button>
          )}

          {!isFirst && (
            <button className="tour-btn ghost" onClick={onBack} type="button">
              Volver
            </button>
          )}

          {!isLast && (
            <button className="tour-btn primary" onClick={onNext} type="button">
              Siguiente
            </button>
          )}
        </div>
      </div>

      {/* ✅ Modales de confirmación e información */}
      <TourConfirmModal
        open={showConfirm}
        onConfirm={() => {
          try {
            localStorage.setItem(tutorialKey, "1");
          } catch { }

          setShowConfirm(false);
          if (typeof onClose === 'function') {
            onClose();
          }

          setShowInfo(true);
        }}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* ===================== Modal bonito de compra de Comodín ===================== */
function BuyComodinModal({
  open,
  item,
  coinsNow = 0,
  onCancel,
  onConfirm,
  confirming = false,
  canBuy = false,
}) {
  if (!open || !item) return null;

  const price = Number(item.price || 0);
  const after = Math.max(0, coinsNow - price);
  const missing = Math.max(0, price - coinsNow);
  const name = item.nombre || "Comodín";

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2000, padding: 20,
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%", maxWidth: 400,
          boxShadow: "0 20px 60px rgba(0,0,0,.18)",
          animation: "modalIn .18s ease-out",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header morado */}
        <div style={{
          background: "linear-gradient(135deg, #7c3aed, #9333ea)",
          padding: "20px 20px 18px",
          display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              background: "rgba(255,255,255,.2)",
              borderRadius: "50%", width: 38, height: 38,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18,
            }}>✨</div>
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: "1rem" }}>
                Comprar Comodín
              </div>
              <div style={{ color: "rgba(255,255,255,.75)", fontSize: ".75rem", fontWeight: 600 }}>
                {name}
              </div>
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{
              background: "rgba(255,255,255,.2)", border: "none",
              borderRadius: 8, padding: 6, cursor: "pointer",
              display: "flex", alignItems: "center", color: "#fff",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Cuerpo */}
        <div style={{ padding: "20px 20px 8px", display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>

          {/* Icono centrado */}
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "#f3e8ff",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Zap size={30} style={{ color: "#9333ea" }} />
          </div>

          {/* Título + descripción centrados */}
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: "1.1rem", fontWeight: 900, color: "#1e293b" }}>
              {name}
            </h3>
            <p style={{ margin: 0, color: "#475569", fontSize: ".88rem", lineHeight: 1.6 }}>
              Usa este comodín para ayudarte a <strong>seguir avanzando</strong> en esta tarea
              cuando las vidas o las preguntas se compliquen.
            </p>
          </div>

          {/* Stats en filas */}
          <div style={{ width: "100%", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
            {[
              { label: "Saldo actual", value: `${coinsNow} UTMCoins`, color: "#1e293b" },
              { label: "Precio", value: `-${price} UTMCoins`, color: "#dc2626" },
              { label: "Saldo tras la compra", value: `${after} UTMCoins`, color: "#16a34a", bold: true },
            ].map(({ label, value, color, bold }, i, arr) => (
              <div key={label} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "11px 16px",
                borderBottom: i < arr.length - 1 ? "1px solid #e2e8f0" : "none",
                fontWeight: bold ? 800 : 600,
              }}>
                <span style={{ color: "#64748b", fontSize: ".85rem", fontWeight: bold ? 800 : 600 }}>{label}</span>
                <span style={{ color, fontSize: ".9rem", fontWeight: 800 }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Warning */}
          {!canBuy && (
            <div style={{
              width: "100%", background: "#fff7ed", border: "1px solid #fed7aa",
              borderRadius: 10, padding: "10px 14px",
              color: "#c2410c", fontSize: ".82rem", fontWeight: 600,
            }}>
              Te faltan <strong>{missing}</strong> UTMCoins para esta compra.
            </div>
          )}

          {/* Nota */}
          <p style={{ margin: 0, color: "#94a3b8", fontSize: ".75rem", textAlign: "center", lineHeight: 1.5 }}>
            El comodín se aplica <strong style={{ color: "#64748b" }}>solo al componente actual</strong> y esta operación
            <strong style={{ color: "#64748b" }}> no es reembolsable</strong>.
          </p>
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", gap: 10,
          padding: "12px 20px 20px",
        }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: "10px 16px", borderRadius: 12,
              border: "1px solid #e2e8f0", background: "#fff",
              color: "#374151", fontWeight: 700, fontSize: ".88rem", cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={confirming || !canBuy}
            style={{
              flex: 1, padding: "10px 16px", borderRadius: 12,
              border: "none",
              background: canBuy ? "linear-gradient(135deg,#7c3aed,#9333ea)" : "#e2e8f0",
              color: canBuy ? "#fff" : "#94a3b8",
              fontWeight: 700, fontSize: ".88rem",
              cursor: canBuy ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {confirming ? "Comprando…" : "Confirmar compra"}
          </button>
        </div>
      </div>
    </div>
  );
}

const ComponenteTareas = () => {
  const { codigoCurso, structureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);

  const {
    fetchCourses,
    getCourseStructureStudent,
    studentProgress = {},
    progressLoading,
    isElementCompleted,
    isElementUnlocked,
    getElementProgress,
    getTaskActivityProgress,
    utmCoins,
    initComodinIfAbsent,
    getComodinesState,
    useComodin: callComodin,
    fetchStoreItems,
    buyStoreItem,
    refreshUTMCoins,
  } = useFuncion();

  const [curso, setCurso] = useState(null);
  const [estructura, setEstructura] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstComponentId, setFirstComponentId] = useState(null);

  // Modal de tareas (Detalles)
  const [taskModal, setTaskModal] = useState({ open: false, tarea: null, actividades: [] });

  /* ===== Racha local ===== */
const [openStreak, setOpenStreak] = useState(false);
const [taskFilter, setTaskFilter] = useState("all");
const [showTaskFilter, setShowTaskFilter] = useState(false);

  /* ===== Estados del Tutorial ===== */
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourLockClick, setTourLockClick] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showTourInfo, setShowTourInfo] = useState(false);

  /* ===== Estados de compra de comodín ===== */
  const [comodin, setComodin] = useState(null);
  const [comodinLoading, setComodinLoading] = useState(false);
  const [buyingComodin, setBuyingComodin] = useState(false);
  const [comodinMsg, setComodinMsg] = useState({ type: "", msg: "" });
  const [confirmBuyOpen, setConfirmBuyOpen] = useState(false);
  const [comodinesRefreshKey, setComodinesRefreshKey] = useState(0);
  const [progressRefreshTick, setProgressRefreshTick] = useState(0);
  const [localCompletedIds, setLocalCompletedIds] = useState(() => new Set());

  const { user } = useAuth();
  const userKey = useMemo(
    () =>
      user?.local?.id ||
      user?.estudiante_id ||
      user?.student_id ||
      user?.id ||
      user?.email ||
      getUserKeyFromStorage(),
    [user?.local?.id, user?.estudiante_id, user?.student_id, user?.id, user?.email]
  );
  const userScope = useMemo(() => cacheManager.safeKey(userKey || "anon"), [userKey]);
  const tutorialKey = useMemo(
    () => `wt:tutorial-tareas:v1:${codigoCurso}:${userKey}`,
    [codigoCurso, userKey]
  );

  useEffect(() => {
    const onStudentProgressUpdated = (event) => {
      const detail = event?.detail || {};
      if (detail?.userScope && detail.userScope !== userScope) return;
      if (
        detail?.codigoCurso &&
        String(detail.codigoCurso) !== String(codigoCurso)
      ) {
        return;
      }
      if (detail?.completedTaskId != null) {
        setLocalCompletedIds((prev) => {
          const next = new Set(prev);
          next.add(Number(detail.completedTaskId));
          return next;
        });
      }
      setProgressRefreshTick((value) => value + 1);
    };

    window.addEventListener("student-course-progress-updated", onStudentProgressUpdated);
    return () =>
      window.removeEventListener("student-course-progress-updated", onStudentProgressUpdated);
  }, [codigoCurso, userScope]);

  useEffect(() => {
    setLocalCompletedIds(new Set());
  }, [codigoCurso, userScope, structureId]);

  // Cargar comodín disponible
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setComodinLoading(true);
        const res = await fetchStoreItems?.({ onlyActive: true }).catch(() => ({}));
        const raw = Array.isArray(res?.items) ? res.items : res?.products || [];
        const first = raw.find((it) => String(it?.categoria ?? it?.tipo ?? "").toLowerCase() === "comodin");
        // ✅ LÓGICA CORREGIDA
        if (alive && first) {
          let stockValue;

          // Si tiene stock infinito explícito → null
          if (first.stock_infinito === true || first.stock_infinito === 1 || first.stock_infinito === "1") {
            stockValue = null; // Infinito
          } else {
            // Stock finito → usar el valor real (incluso si es 0)
            stockValue = Number(first.stock_actual ?? first.stock ?? 0);
          }

          const comodinData = {
            id: first.id,
            nombre: first.nombre || first.name || "Comodín",
            price: Number(first.precio_coins ?? first.price_coins ?? first.price ?? 0),
            activo: (first.activo ?? first.active ?? true) === true,
            stock: stockValue, // ✅ Ahora puede ser null (infinito) o número (finito)
            limit_per_user: first.limit_per_user ?? first.limit ?? null,
          };

          setComodin(comodinData);
        }
      } finally {
        alive && setComodinLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [fetchStoreItems]);

  /* ===== Pasos del Tutorial ===== */
  const TOUR_STEPS = useMemo(
    () => [
      // ✅ PASO 1: Bienvenida (centrado, sin target)
      {
        key: "welcome",
        title: "BIENVENIDO AL COMPONENTE DE TAREAS",
        text:
          "En esta sección completarás tareas que contienen varias actividades.\n\n" +
          "Cada actividad puede contener preguntas, juegos o ambas. ¡Vamos a conocer cómo funciona!",
        target: null,
        centered: true,
      },

      // ✅ PASO 2: Hero enfocado (solo nombre + progreso)
      {
        key: "hero",
        title: "INFORMACION DEL COMPONENTE",
        text:
          "Aquí se mostrará el nombre del componente y tu progreso general.\n\n" +
          "La barra de progreso muestra cuánto has avanzado. Cuando llegues al 100% podrás continuar al siguiente componente.",
        target: () => document.querySelector("[data-tour='hero-header']"),
      },

      // ✅ PASO 3: Monedas (ahora primero)
      {
        key: "coins_store",
        title: "UTMCOINS",
        text:
          "Las UTMCoins son la moneda del sistema. Las conseguiras al completar tareas y tests.\n\n" +
          "Úsalas para comprar comodines que te ayudarán en las tareas.",
        target: () => document.querySelector("[data-tour='coins-card']"),
      },

      //  PASO 4: Comodines (ahora segundo)
      {
        key: "comodines_detail",
        title: "COMODINES DEL COMPONENTE",
        text:
          "Los comodines que compres o ganes se verán reflejados en el ÚLTIMO COMPONENTE DESBLOQUEADO.\n\n" +
          "Si deseas tener más comodines haz clic en el botón morado '+' para comprar cuando los necesites.",
        target: () => document.querySelector("[data-tour='comodin-card']"),
        customClass: "tour-card-comodines",
      },
      {
        key: "first_task_preview",
        title: "TU PRIMERA TAREA",
        text:
          "Cada componente contiene tareas. Las tareas agrupan actividades que debes completar.\n\n" +
          "Podrás ver la cantidad de preguntas o juegos que contiene. En DETALLES verás breve descripción de la misma.",
        target: () => document.querySelector("[data-tour='first-task']"),
        blockClicks: true,
        customClass: "tour-card-left-bottom",  // 
      },

      {
        key: "start_task",
        title: "¡COMIENZA TU PRIMERA TAREA!",
        text:
          "Antes de iniciar una actividad debes tomar en cuenta lo siguiente:\n\n" +
          "Dentro de la tarea podrás comprar comodines si los necesitas\n\n" +
          "• Si completas la actividad al primer intento ganarás un extra de UTMCoins\n\n" +
          "¡Así que haz tu mejor esfuerzo! " +
          "¡Buena suerte! ",
        target: () => document.querySelector("[data-tour='start-btn']"),
        allowClick: true,
        customClass: "tour-card-final-start",
      },
    ],
    []
  );

  /* ===== Carga de curso/estructura ===== */
  useEffect(() => {
    let alive = true;
    setLoading(true);

    (async () => {
      try {
        // ── 1) Cache de metadatos del curso ──────────────────────
        const cacheKeyMeta = cacheManager.getCoursMetaCacheKey(codigoCurso, userScope);
        const cachedCourse = cacheManager.readCache(cacheKeyMeta, {
          storageType: 'session',
          currentVersion: cacheManager.CURRENT_VERSIONS.COURSE_META,
        });

        let found = cachedCourse?.course ?? null;
        if (found && alive) setCurso(found);

        // ── 2) Si tenemos curso, intentar pintar estructura desde cache ──
        // ── 3) Revalidar curso si no está fresco ─────────────────
        if (!cachedCourse?.isFresh) {
          try {
            const courseData = await fetchCourses({ search: codigoCurso });
            if (!alive) return;
            found = courseData?.courses?.find((c) => c.codigo_curso === codigoCurso) ?? null;
            if (alive) setCurso(found);

            if (found) {
              cacheManager.writeCache(
                cacheKeyMeta,
                { course: found },
                {
                  storageType: 'session',
                  ttl: cacheManager.DEFAULT_TTL.COURSE_META,
                  cacheVersion: cacheManager.CURRENT_VERSIONS.COURSE_META,
                }
              );
            }
          } catch (e) {
            console.warn('ComponenteTareas: no se pudo revalidar curso, usando cache.', e);
          }
        }

        if (!alive) return;
        if (!found) { setLoading(false); return; }

        // ── 4) Estructura: leer cache ─────────────────────────────
        const cacheKeyStruct = cacheManager.getStructureCacheKey(found.id, userScope);
        const cachedStruct = cacheManager.readCache(cacheKeyStruct, {
          storageType: 'local',
          currentVersion: cacheManager.CURRENT_VERSIONS.COURSE_STRUCTURE,
          validateVersion: true,
        });

        // ── 5) Revalidar estructura desde backend ─────────────────
        try {
          const structureData = await getCourseStructureStudent(found.id);
          if (!alive) return;
          const struct = structureData?.structure || [];

          const raw = JSON.stringify(struct);
          const newHash = cacheManager.hashString(raw);
          const oldHash = cachedStruct?.hash ?? null;
          const firstId = getFirstComponentIdFromStructure(struct);

          setEstructura(struct);
          setFirstComponentId(firstId);
          setLoading(false);

          if (oldHash !== newHash) {
            // Calcular firstComponentId
            cacheManager.writeCache(
              cacheKeyStruct,
              { structure: struct, firstComponentId: firstId, hash: newHash },
              {
                storageType: 'local',
                ttl: cacheManager.DEFAULT_TTL.COURSE_STRUCTURE,
                cacheVersion: cacheManager.CURRENT_VERSIONS.COURSE_STRUCTURE,
                includeStructureVersion: true,
              }
            );
          } else if (cachedStruct) {
            // Mismo contenido: solo refrescar TTL
            cacheManager.writeCache(
              cacheKeyStruct,
              {
                structure: cachedStruct.structure,
                firstComponentId: cachedStruct.firstComponentId,
                hash: oldHash,
              },
              {
                storageType: 'local',
                ttl: cacheManager.DEFAULT_TTL.COURSE_STRUCTURE,
                cacheVersion: cacheManager.CURRENT_VERSIONS.COURSE_STRUCTURE,
                includeStructureVersion: true,
              }
            );
          }
        } catch (e) {
          console.warn('ComponenteTareas: no se pudo actualizar estructura, usando cache.', e);
        } finally {
          if (alive) setLoading(false);
        }

      } catch (e) {
        console.error('ComponenteTareas: error crítico cargando datos:', e);
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [codigoCurso, fetchCourses, getCourseStructureStudent, userScope, progressRefreshTick]);

  useEffect(() => {
    cacheManager.clearExpiredCache();
  }, []);

  const selectedComponent = useMemo(() => {
    const id = parseInt(structureId, 10);
    return collectNodesByType(estructura, "componente").find((c) => c.id === id);
  }, [estructura, structureId]);

  // 🧩 Lista plana de componentes (como en CursoDetalle)
  const componentsFlat = useMemo(
    () => collectNodesByType(estructura, "componente"),
    [estructura]
  );

  // 🧩 Contexto por componente: nivel, módulo, siblings...
  const componentContext = useMemo(() => {
    const ctx = new Map();

    (estructura || []).forEach((niv) => {
      if (!Array.isArray(niv?.children)) return;

      const directComponents = niv.children.filter((c) => c && c.tipo === "componente");
      directComponents.forEach((c, index) => {
        ctx.set(c.id, {
          nivel: niv,
          modulo: null,
          index,
          siblings: directComponents,
        });
      });

      niv.children.forEach((mod) => {
        if (!mod || mod.tipo !== "modulo" || !Array.isArray(mod.children)) return;

        const comps = mod.children.filter((c) => c && c.tipo === "componente");

        comps.forEach((c, index) => {
          ctx.set(c.id, {
            nivel: niv,
            modulo: mod,
            index,
            siblings: comps,
          });
        });
      });
    });

    return ctx;
  }, [estructura]);




  const isUnlocked = useCallback(
    (item) => {
      if (!curso || !item) return false;

      // ✅ Primer componente del curso SIEMPRE accesible
      // Estado base que viene del backend
      const baseUnlocked = isElementUnlocked(curso.id, item.id);

      // ✅ Lógica ESPECIAL solo para COMPONENTES (igual que en CursoDetalle)
      if (item.tipo === "componente") {
        const ctx = componentContext.get(item.id);
        const modulo = ctx?.modulo;

        if (modulo) {
          const rawSeq = modulo.secuencia_hijos ?? modulo.secuencial_hijos;
          const seqModulo = flagToBoolean(rawSeq, true);

          // 🟢 Módulo NO secuencial: si el módulo está desbloqueado
          // o al menos un componente está desbloqueado, liberamos todos
          if (!seqModulo) {
            const siblings = ctx.siblings || [];

            const moduloUnlocked = isElementUnlocked(curso.id, modulo.id);

            const anyUnlockedInModule = siblings.some((comp) => {
              return isElementUnlocked(curso.id, comp.id);
            });

            if (moduloUnlocked || anyUnlockedInModule) {
              return true;
            }

            return baseUnlocked;
          }

          // 🔒 Módulo secuencial → respetamos lo que diga el backend
          return baseUnlocked;
        }
      }

      // 🔒 Niveles, módulos, etc: el backend manda
      return baseUnlocked;
    },
    [curso, isElementUnlocked, componentContext]
  );


  useEffect(() => {
    if (!loading && !progressLoading) {
      if (!selectedComponent || !isUnlocked(selectedComponent)) {
        navigate(`/curso/${codigoCurso}`, { replace: true });
      }
    }
  }, [loading, progressLoading, selectedComponent, codigoCurso, navigate, isUnlocked]);

  /* ===== Helpers actividades ===== */
  const collectTaskActivities = (tareaObj) => {
    if (!tareaObj) return [];

    const seen = new Set();
    const items = [];

    const pushAct = (raw, tipo_actividad, idxFallback = null) => {
      if (!raw) return;
      const ta = String(tipo_actividad || raw.tipo_actividad || "").toLowerCase();

      // Solo contamos preguntas/juegos activos
      if (ta !== "pregunta" && ta !== "juego") return;
      if (raw.activo === false) return;

      // Id robusto
      let id = raw.id;
      if (id == null || id === "") {
        id =
          ta === "juego"
            ? `juego-${tareaObj.id}-${idxFallback ?? Math.random().toString(36).slice(2)}`
            : `pregunta-${tareaObj.id}-${idxFallback ?? Math.random().toString(36).slice(2)}`;
      }

      const key = `${ta}:${id}`;
      if (seen.has(key)) return;
      seen.add(key);

      items.push({
        ...raw,
        id,
        tipo: "actividad",
        tipo_actividad: ta,
        parent_id: tareaObj.id,
        _label: ta === "juego" ? "Juego" : "Pregunta",
      });
    };

    // 1) Prioridad: hijos estructurados
    const childActs = Array.isArray(tareaObj.children)
      ? tareaObj.children.filter((ch) => ch?.tipo === "actividad")
      : [];

    childActs.forEach((a) => pushAct(a, a?.tipo_actividad));

    // 2) Si no hay hijos válidos, caemos a preguntas/juegos planos
    if (items.length === 0) {
      (Array.isArray(tareaObj.preguntas) ? tareaObj.preguntas : []).forEach((p, idx) =>
        pushAct(p, "pregunta", idx)
      );
      (Array.isArray(tareaObj.juegos) ? tareaObj.juegos : []).forEach((g, idx) =>
        pushAct(g, "juego", idx)
      );
    }

    return items;
  };

  const buildActividadesVM = useCallback(
    (tarea) => {
      const actividadesRaw = collectTaskActivities(tarea);
      return actividadesRaw.map((act) => ({
        ...act,
        _label: act.tipo_actividad === "juego" ? "Juego" : "Pregunta",
        _completed: curso && act?.id ? !!isElementCompleted(curso.id, act.id) : false,
      }));
    },
    [curso, isElementCompleted]
  );

  const getActivityCountsAndProgress = useCallback(
    (tarea) => {
      if (!curso || !tarea) {
        return {
          completed: 0,
          total: 0,
          counts: { preguntas: 0, juegos: 0 },
        };
      }

      const actividades = collectTaskActivities(tarea);

      const counts = actividades.reduce(
        (acc, a) => {
          if (a.tipo_actividad === "pregunta") acc.preguntas++;
          else if (a.tipo_actividad === "juego") acc.juegos++;
          return acc;
        },
        { preguntas: 0, juegos: 0 }
      );

      try {
        const { completed, total } = getTaskActivityProgress(curso.id, tarea.id, actividades);
        return { completed, total, counts };
      } catch {
        const completedCount = actividades.filter((a) => isElementCompleted(curso.id, a.id)).length;
        return {
          completed: completedCount,
          total: actividades.length,
          counts,
        };
      }
    },
    [curso, getTaskActivityProgress, isElementCompleted]
  );

  // progreso real del componente (para el ring grande)
  const calculateRealProgress = useCallback(
    (item) => (curso ? getElementProgress(curso.id, item.id) : 0),
    [curso, getElementProgress]
  );

  const componentProgress = useMemo(() => {
    if (!selectedComponent) return 0;
    return Math.round(calculateRealProgress(selectedComponent));
  }, [selectedComponent, calculateRealProgress]);

  const [ringValue, setRingValue] = useState(0);
  useEffect(() => {
    const t0 = performance.now();
    const start = ringValue;
    const end = componentProgress;
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / 800);
      setRingValue(Math.round(start + (end - start) * easeOutCubic(p)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [componentProgress, ringValue]);

  const isCompletedItem = useCallback(
    (item) => {
      if (!curso || !item) return false;
      if (localCompletedIds.has(Number(item.id))) return true;
      return isElementCompleted(curso.id, item.id);
    },
    [curso, isElementCompleted, localCompletedIds]
  );
  const nextComponent = useMemo(() => {
    if (!selectedComponent) return null;
    const idx = componentsFlat.findIndex((c) => c.id === selectedComponent.id);
    if (idx >= 0 && idx + 1 < componentsFlat.length) return componentsFlat[idx + 1];
    return null;
  }, [componentsFlat, selectedComponent]);

  const showNextBtn = componentProgress >= 100 && nextComponent && isUnlocked(nextComponent);

  const goNext = useCallback(() => {
    if (!nextComponent) return;
    navigate(`/curso/${codigoCurso}/tarea/${nextComponent.id}`);
  }, [navigate, codigoCurso, nextComponent]);

  /* ===== Abrir modal de una tarea ===== */
  const openTaskModal = useCallback(
    (tarea) => {
      if (!tarea) return;
      const actividadesVM = buildActividadesVM(tarea);
      const tareaProgress = studentProgress[curso.id]?.[tarea.id];
      const { completed: c, total: t } = getActivityCountsAndProgress(tarea);
      const done = isCompletedItem(tarea) || (!!t && c === t) || !!tareaProgress?.fecha_completado;
      setTaskModal({
        open: true,
        tarea: { ...tarea, _done: done },
        actividades: actividadesVM,
      });
    },
    [buildActividadesVM]
  );

  const closeTaskModal = useCallback(
    () =>
      setTaskModal({
        open: false,
        tarea: null,
        actividades: [],
      }),
    []
  );

  /* ===== Calcular si una tarea YA estaba completada ===== */
  const wasTaskCompletedBefore = useCallback(
    (tarea) => {
      if (!tarea || !curso) return false;

      const { completed, total } = getActivityCountsAndProgress(tarea);
      const tareaProgress = studentProgress[curso.id]?.[tarea.id];

      const doneNow =
        isCompletedItem(tarea) || (!!total && completed === total) || !!tareaProgress?.fecha_completado;

      return !!doneNow;
    },
    [curso, studentProgress, getActivityCountsAndProgress, isCompletedItem]
  );

  const ensureLivesOnEnter = useCallback(
    async (compId) => {
      if (!compId) return;
      try {
        await initComodinIfAbsent?.(compId);
      } catch {
        /* no-op */
      }
    },
    [initComodinIfAbsent]
  );

  const checkVidasForTask = useCallback(
    async (tareaId) => {
      try {
        const st = await getComodinesState?.({ tareaId }).catch(() => null);
        const vidas = Number(st?.vidas_restantes ?? 0);
        const comodines = Number(st?.comodines_restantes ?? 0);
        const compId = Number(st?.componente_id ?? 0);
        return { ok: true, vidas, comodines, compId };
      } catch (e) {
        console.error("Error verificando vidas de la tarea:", e);
        return { ok: false, vidas: 0, comodines: 0, compId: 0 };
      }
    },
    [getComodinesState]
  );

  /* ===== Iniciar tarea (con bloqueo de tutorial) ===== */
  const tareas = useMemo(() => {
    return selectedComponent?.children?.filter((t) => t.tipo === "tarea") || [];
  }, [selectedComponent]);


  // ✅ Secuencia de tareas dentro del componente (si secuencia_hijos=true)
  const secuenciaTareas = useMemo(() => {
    // OJO: default false para no romper componentes antiguos
    const raw = selectedComponent?.secuencia_hijos ?? selectedComponent?.secuencial_hijos;
    return flagToBoolean(raw, false);
  }, [selectedComponent]);

  // ✅ Reutilizable: determinar si una tarea está "done" (misma lógica que usas en el render)
  const isTaskDone = useCallback(
    (tarea) => {
      if (!curso || !tarea) return false;

      const { completed, total } = getActivityCountsAndProgress(tarea);
      const tareaProgress = studentProgress[curso.id]?.[tarea.id];

      return (
        isCompletedItem(tarea) ||
        (!!total && completed === total) ||
        !!tareaProgress?.fecha_completado
      );
    },
    [curso, studentProgress, getActivityCountsAndProgress, isCompletedItem]
  );

  // ✅ Índice de la primera tarea NO completada (si todas están completas => tareas.length)
  const firstPendingTaskIndex = useMemo(() => {
    if (!secuenciaTareas) return -1;
    for (let i = 0; i < tareas.length; i++) {
      if (!isTaskDone(tareas[i])) return i;
    }
    return tareas.length; // todas completas
  }, [secuenciaTareas, tareas, isTaskDone]);

  // ✅ Determinar si una tarea está desbloqueada por secuencia
  const isTaskUnlockedBySequence = useCallback(
    (tarea, idx) => {
      if (!secuenciaTareas) return true;
      if (!tarea) return false;

      const i = Number.isFinite(idx) ? idx : tareas.findIndex((x) => x.id === tarea.id);

      // Si no encontramos índice, por seguridad lo bloqueamos
      if (i < 0) return false;

      // Solo permitimos iniciar: tareas anteriores + la primera pendiente
      // (si firstPendingTaskIndex === tareas.length => todas desbloqueadas)
      return i <= firstPendingTaskIndex;
    },
    [secuenciaTareas, tareas, firstPendingTaskIndex]
  );


  const handleStartTask = useCallback(
    async (tarea, mode = "start", tareaIndex = null) => {
      if (!tarea) return;

      // ✅ En secuencia: bloquear inicio si no es la primera pendiente (o anterior)
      const idx =
        Number.isFinite(tareaIndex) && tareaIndex != null
          ? tareaIndex
          : tareas.findIndex((t) => t.id === tarea.id);

      if (!isTaskUnlockedBySequence(tarea, idx)) {
        alert("Debes completar las tareas anteriores antes de iniciar esta.");
        return;
      }

      // ✅ Bloqueo del tutorial SOLO si está abierto
      if (tourOpen) {
        const isFinalStep = TOUR_STEPS?.[tourStep]?.key === "start_task";

        if (isFinalStep) {
          const firstTask = tareas[0];
          if (tarea.id !== firstTask?.id) {

            setTourLockClick(true);
            return;
          }


          try {
            localStorage.setItem(tutorialKey, "1");
          } catch { }

          setTourOpen(false);
          setTourLockClick(false);
        } else {

          return;
        }
      }

      // ===== LÓGICA NORMAL DE INICIO DE TAREA =====


      try {
        if (selectedComponent?.id) {
          await ensureLivesOnEnter(selectedComponent.id);
        }
      } catch (e) {
        console.error("Error asegurando vidas:", e);
      }

      const firstCheck = await checkVidasForTask(tarea.id);
      if (!firstCheck.ok) {
        alert("No se pudo verificar tus vidas para esta tarea. Intenta nuevamente.");
        return;
      }

      let { vidas, comodines, compId } = firstCheck;


      if (vidas <= 0) {
        console.warn("⚠️ Sin vidas disponibles");

        if (comodines > 0 && compId) {
          try {

            await callComodin?.(String(compId), {
              kind: "vida",
              tareaId: tarea.id,
              componenteId: compId,
            });

            const secondCheck = await checkVidasForTask(tarea.id);
            if (!secondCheck.ok) {
              alert("No se pudo verificar tus vidas luego de usar un comodín. Intenta nuevamente.");
              return;
            }
            vidas = secondCheck.vidas;

          } catch (e) {
            console.error("Error usando comodín de vida:", e);
            alert("Error al usar el comodín de vida. Intenta nuevamente.");
            return;
          }
        }

        if (vidas <= 0) {
          alert("No tienes vidas para esta tarea hoy. Compra/usa un comodín de vida para continuar.");
          return;
        }
      }

      const actividadesVM = buildActividadesVM(tarea);
      const alreadyCompleted = wasTaskCompletedBefore(tarea);

      navigate(`/curso/${codigoCurso}/tarea/${tarea.id}/resolver`, {
        state: {
          cursoId: curso?.id,
          componentId: selectedComponent?.id,
          tarea,
          actividades: actividadesVM,
          mode,
          alreadyCompleted,
        },
      });
    },
    [
      buildActividadesVM,
      wasTaskCompletedBefore,
      navigate,
      codigoCurso,
      curso?.id,
      selectedComponent,
      ensureLivesOnEnter,
      checkVidasForTask,
      callComodin,
      tourOpen,
      tourStep,
      TOUR_STEPS,
      tutorialKey,
      tareas,
      isTaskUnlockedBySequence, // ✅ NUEVO
    ]
  );

  /* ===== Detectar regreso después de completar primera tarea ===== */
  useEffect(() => {
    if (loading || progressLoading) return;
    if (tareas.length === 0) return;

    try {
      const tutorialDone = localStorage.getItem(tutorialKey) === "1";
      if (!tutorialDone) return;

      const firstTaskCompletionKey = `${tutorialKey}:firstTaskCompleted`;
      const alreadyShown = sessionStorage.getItem(firstTaskCompletionKey) === "1";
      if (alreadyShown) return;

      const firstTask = tareas[0];
      if (!firstTask) return;

      const isCompleted = isCompletedItem(firstTask);
      if (isCompleted) {
        sessionStorage.setItem(firstTaskCompletionKey, "1");
        setShowCompletionModal(true);
      }
    } catch { }
  }, [loading, progressLoading, tareas, tutorialKey, isCompletedItem]);

  /* ===== Inicializar tutorial (solo 1 vez en el primer componente) ===== */
  useEffect(() => {
    if (loading || progressLoading) return;
    if (!curso || !selectedComponent) return;
    if (tareas.length === 0) return;

    try {
      const done = localStorage.getItem(tutorialKey) === "1";
      if (done) return;
    } catch { }

    // Abrir tutorial
    setTourStep(0);
    setTourOpen(true);
    setTourLockClick(false);
  }, [loading, progressLoading, curso, selectedComponent, tareas.length, tutorialKey]);

  // ripple visual en botones
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onClick = (e) => {
      const btn = e.target.closest(".btn");
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const ripple = document.createElement("span");
      ripple.className = "ripple";
      const size = Math.max(rect.width, rect.height);
      ripple.style.width = ripple.style.height = size + "px";
      ripple.style.left = e.clientX - rect.left - size / 2 + "px";
      ripple.style.top = e.clientY - rect.top - size / 2 + "px";
      btn.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, []);

  /* ===== Navegación a store ===== */
  const handleGoStore = useCallback(() => {
    navigate(`/curso/${codigoCurso}/store`, {
      state: { from: location },
    });
  }, [navigate, codigoCurso, location]);

  const coinsNow = Number(utmCoins?.cantidad_actual ?? 0);

  const canBuyComodin = useMemo(() => {
    if (!comodin) {

      return false;
    }

    if (!comodin.activo) {

      return false;
    }

    // ✅ LÓGICA CORREGIDA DE STOCK
    const hasStock = comodin.stock === null || // Stock infinito
      comodin.stock === undefined || // No definido (infinito por defecto)
      Number(comodin.stock) > 0; // Stock finito con cantidad disponible

    if (!hasStock) {
      return false;
    }

    const price = Number(comodin.price || 0);
    const hasEnoughCoins = coinsNow >= price;

    if (!hasEnoughCoins) {
      return false;
    }

    return true;
  }, [comodin, coinsNow]);

  const buyOneComodin = useCallback(async () => {
    if (!comodin || !buyStoreItem) return;

    const compId = selectedComponent?.id;
    if (!compId) {
      console.error('❌ No se puede comprar: componentId no disponible');
      alert('Error: No se pudo identificar el componente actual.');
      return;
    }

    try {
      setBuyingComodin(true);
      setComodinMsg({ type: "", msg: "" });

      const res = await buyStoreItem({
        itemId: comodin.id,
        quantity: 1,
        componentId: compId,
      });



      const ok = Boolean(
        res?.ok ??
        res?.success ??
        res?.purchase?.id ??
        res?.data?.purchase?.id ??
        res?.id
      );

      if (ok) {

        setComodinMsg({
          type: "ok",
          msg: `Comodín comprado y aplicado al componente "${selectedComponent?.nombre}".`
        });

        await refreshUTMCoins?.();
        setComodinesRefreshKey((k) => k + 1);

        setTimeout(() => {
          setConfirmBuyOpen(false);
        }, 1500);

      } else {
        const errorMsg = res?.message || res?.error || 'No se pudo completar la compra.';
        console.error('❌ Compra fallida:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (e) {
      console.error('❌ Error en compra de comodín:', e);
      setComodinMsg({
        type: "err",
        msg: e?.message || "Error al comprar el comodín. Intenta nuevamente."
      });
    } finally {
      setBuyingComodin(false);
    }
  }, [comodin, buyStoreItem, refreshUTMCoins, selectedComponent, setComodinesRefreshKey]);

  // ✅ GUARDS DE RENDER (después de todos los hooks)
  if (loading || progressLoading)
    return <LoadingSpinner message="Cargando tareas del componente..." />;

  if (!curso || !selectedComponent) {
    return (
      <div className="error-container">
        <p>No se pudo cargar el componente solicitado.</p>
      </div>
    );
  }

  // tilt card (solo desktop)
  const handleCardMouseMove = (e) => {
    if (window.matchMedia("(max-width: 767px)").matches) return;
    const card = e.currentTarget;
    const r = card.getBoundingClientRect();
    const mx = (e.clientX - r.left) / r.width;
    const my = (e.clientY - r.top) / r.height;
    card.style.setProperty("--rx", `${(0.5 - my) * 6}deg`);
    card.style.setProperty("--ry", `${(mx - 0.5) * 6}deg`);
  };

  const handleCardLeave = (e) => {
    const card = e.currentTarget;
    card.style.setProperty("--rx", `0deg`);
    card.style.setProperty("--ry", `0deg`);
  };

  return (
    <div ref={rootRef} className="task-page">
      {/* ✅ TOPBAR (igual al 1er código) */}
      <header className="task-topbar">
        <button onClick={() => navigate(`/curso/${codigoCurso}`)} className="back-button">
          <ArrowLeft className="w-4 h-4" />
          <span className="hide-xs">Volver</span>
        </button>

        <div className="topbar-center">
          <span className="topbar-label">{curso?.nombre || codigoCurso}</span>
          <span className="topbar-divider">·</span>
          <span className="topbar-sub">{selectedComponent?.nombre}</span>
        </div>

        {showNextBtn ? (
          <button
            className="next-topbar-btn btnGlow"
            onClick={goNext}
            aria-label="Ir al siguiente componente"
            title="Ir al siguiente componente"
          >
            <span className="hide-xs">Siguiente</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <div className="topbar-spacer" />
        )}
      </header>

      <section className="hero-container">
       {/* HEADER IZQUIERDO */}
<div className="hero-left-card" data-tour="hero-header">

  {/* Fila: título + botones */}
  <div className="hero-top-row">
    <h2 className="component-title">
      {selectedComponent.nombre?.toUpperCase()}
    </h2>
    <div className="hero-controls">
      <div style={{ position: "relative" }}>
        <button
          className="ctrl-btn"
          onClick={() => setShowTaskFilter((v) => !v)}
        >
          <Filter size={14} />
          <span className="hide-xs">
            {taskFilter === "all" ? "Filtrar" : taskFilter === "pending" ? "Pendientes" : "Completadas"}
          </span>
        </button>

        {showTaskFilter && (
          <div style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,.10)",
            padding: "6px",
            zIndex: 50,
            minWidth: 150,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}>
            {[
              { val: "all", label: "Todos" },
              { val: "pending", label: "Pendientes" },
              { val: "completed", label: "Completadas" },
            ].map(({ val, label }) => (
              <button
                key={val}
                onClick={() => { setTaskFilter(val); setShowTaskFilter(false); }}
                style={{
                  textAlign: "left",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: ".8rem",
                  background: taskFilter === val ? "#eff6ff" : "transparent",
                  color: taskFilter === val ? "#2563eb" : "#475569",
                }}
                onMouseOver={(e) => { if (taskFilter !== val) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseOut={(e) => { if (taskFilter !== val) e.currentTarget.style.background = "transparent"; }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        className="ctrl-btn"
        onClick={() => {
          const modulo = estructura
            .flatMap((n) => n.children || [])
            .find((m) => m.children?.some((c) => c.id === selectedComponent?.id));
          if (modulo?.id) {
            navigate(`/curso/${codigoCurso}/modulo/${modulo.id}/recursos`, {
              state: { from: location.pathname },
            });
          }
        }}
      >
        <FolderOpen size={14} />
        <span className="hide-xs">Recursos</span>
        {selectedComponent?.recursos_count > 0 && (
          <span className="count-badge">{selectedComponent.recursos_count}</span>
        )}
      </button>
    </div>
  </div>

  {/* Barra de progreso */}
  <div className="hero-progress-wrap">
    <div className="hero-progress-labels">
      <span className="hero-progress-label">PROGRESO DEL COMPONENTE</span>
      <span className="hero-progress-pct" style={{
        color: ringValue >= 100 ? "#16a34a" : ringValue >= 50 ? "#ca8a04" : "#f97316"
      }}>
        {ringValue}%
      </span>
    </div>
    <div className="hero-progress-track">
      <div
        className="hero-progress-fill"
        style={{
          width: `${ringValue}%`,
          background: ringValue >= 100
            ? "linear-gradient(90deg,#22c55e,#16a34a)"
            : ringValue >= 50
              ? "linear-gradient(90deg,#facc15,#a3e635)"
              : "linear-gradient(90deg,#fb923c,#facc15)",
        }}
      />
    </div>
  </div>
</div>

        {/* HEADER DERECHO */}
        <div className="hero-right-card">
          <GamificationStats
            variant="component"
            componentId={selectedComponent?.id}
            refreshKey={comodinesRefreshKey} // ✅ AGREGAR ESTA LÍNEA
            onCoinsClick={() => setConfirmBuyOpen(true)}
          />
        </div>
      </section>



       {/* ===== LISTA DE TAREAS ===== */}
      <section className="tasks-wrapper">
        {tareas.length > 0 ? (
          <div className="tasks-grid">
            {tareas
  .filter((tarea) => {
    if (taskFilter === "all") return true;
    const tp = studentProgress[curso.id]?.[tarea.id];
    const { completed: c, total: t } = getActivityCountsAndProgress(tarea);
    const done = isCompletedItem(tarea) || (!!t && c === t) || !!tp?.fecha_completado;
    return taskFilter === "completed" ? done : !done;
  })
  .map((tarea, idx) => {
    const realIdx = tareas.findIndex((t) => t.id === tarea.id);
              const { completed, total, counts } = getActivityCountsAndProgress(tarea);
              const tareaProgress = studentProgress[curso.id]?.[tarea.id];
              const done =
                isCompletedItem(tarea) ||
                (!!total && completed === total) ||
                !!tareaProgress?.fecha_completado;
 
              const lockedBySequence =
                secuenciaTareas &&
                firstPendingTaskIndex >= 0 &&
                realIdx > firstPendingTaskIndex;
 
              const hasActivities = total > 0;
 
              return (
                <article
                  key={tarea.id}
                  className={`task-card ${done ? "completed" : lockedBySequence ? "locked" : "pending"}`}
                  style={{ animationDelay: `${idx * 60}ms` }}
                  onMouseMove={handleCardMouseMove}
                  onMouseLeave={handleCardLeave}
                  data-tour={realIdx === 0 ? "first-task" : undefined}
                >
                  {/* Barra lateral de color */}
                  <div className={`task-side-bar ${done ? "bar-done" : lockedBySequence ? "bar-locked" : "bar-pending"}`} />
 
                  {/* Fila superior: estado + fecha */}
                  <div className="task-header-top">
                    <span className={`task-status-pill ${done ? "status-done" : lockedBySequence ? "status-locked" : "status-pending"}`}>
                      {done
                        ? <><BadgeCheck className="w-3 h-3" /> Completada</>
                        : lockedBySequence
                          ? <><Lock className="w-3 h-3" /> Bloqueada</>
                          : <><ClipboardList className="w-3 h-3" /> Pendiente</>
                      }
                    </span>
 
                    {tareaProgress?.fecha_completado && (
                      <div className="completion-info">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(tareaProgress.fecha_completado).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
 
                  {/* Título */}
                  <h3 className="task-title">
                    T#{realIdx + 1}: {tarea.nombre?.toUpperCase()}
                  </h3>
 
                  {/* Chips: preguntas y juegos — recuadro horizontal unificado */}
                  <div className="task-chips-row" data-tour={realIdx === 0 ? "task-chips" : undefined}>
                    {counts.preguntas > 0 && (
                      <div className="task-chip-item">
                        <HelpCircle className="w-3.5 h-3.5 chip-icon-q" />
                        <span>{counts.preguntas} PREGUNTAS</span>
                      </div>
                    )}
                    {counts.preguntas > 0 && counts.juegos > 0 && (
                      <div className="task-chip-divider" />
                    )}
                    {counts.juegos > 0 && (
                      <div className="task-chip-item">
                        <Gamepad2 className="w-3.5 h-3.5 chip-icon-g" />
                        <span>{counts.juegos} JUEGOS</span>
                      </div>
                    )}
                  </div>
 
                  {/* Botones */}
                  <div className="task-actions">
                    <div
                      className={`start-wrap ${lockedBySequence ? "is-locked" : ""}`}
                      data-stop-card-click
                      data-tour={realIdx === 0 ? "start-btn" : undefined}
                    >
                      <StartActivityButton
                        onStart={() => {
                          if (lockedBySequence) return;
                          handleStartTask(tarea, "start", realIdx);
                        }}
                        disabled={lockedBySequence}
                        label={
                          lockedBySequence
                            ? "Bloqueada"
                            : done
                              ? "Reintentar"
                              : "Iniciar"
                        }
                        isDone={done}
                        isLocked={lockedBySequence}
                      />
                    </div>
 
                    <button
                      className={`btn btn-ghost ${lockedBySequence ? "btn-locked" : ""}`}
                      onClick={() => openTaskModal(tarea)}
                      data-stop-card-click
                      title="Ver detalles"
                    >
                      Detalles
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="no-tasks">
            <Package className="w-10 h-10 text-gray-400" />
            <p>Este componente aún no tiene tareas.</p>
          </div>
        )}
      </section>


      {/* ===== Tutorial Overlay ===== */}
      <TourOverlay
        open={tourOpen}
        step={tourStep}
        steps={TOUR_STEPS}
        targetEl={TOUR_STEPS?.[tourStep]?.target?.()}
        showInfo={showTourInfo}
        setShowInfo={setShowTourInfo}
        tutorialKey={tutorialKey}
        onBack={() => setTourStep((s) => Math.max(0, s - 1))}
        onNext={() => setTourStep((s) => Math.min(TOUR_STEPS.length - 1, s + 1))}
        onClose={() => {
          try {
            localStorage.setItem(tutorialKey, "1");
          } catch { }
          setTourOpen(false);
        }}
      />

      <TourInfoModal
        open={showTourInfo}
        onClose={() => setShowTourInfo(false)}
      />

      {/* ===== Modal de Tarea Completada ===== */}
      {
        showCompletionModal && (
          <div className="completion-modal-backdrop" onClick={() => setShowCompletionModal(false)}>
            <div className="completion-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="completion-modal-head">
                <BadgeCheck size={40} className="completion-icon" />
                <h3>¡Tarea Completada!</h3>
              </div>

              <div className="completion-modal-body">
                <p>
                  Puedes volver a repetir esta tarea cuando quieras, pero{" "}
                  <strong>no ganarás monedas adicionales</strong>.
                </p>
                <p>
                  No olvides que cada vez que resuelvas una tarea o test de nivel{" "}
                  <strong>podrás reclamar la racha, ¿Qué esperas para reclamarla?</strong>.
                </p>
                <p className="completion-help-hint">
                  Si quieres volver a ver esta información, dirígete al apartado de{" "}
                  <strong>"Ayuda"</strong>.
                </p>
              </div>

              <div className="completion-modal-actions">
                <button
                  className="tour-btn primary"
                  onClick={() => setShowCompletionModal(false)}
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* ===== Modales ===== */}
      <TareasModal
        open={taskModal.open}
        onClose={closeTaskModal}
        tarea={taskModal.tarea}
        actividades={taskModal.actividades}
        onStart={() => {
          const idx = tareas.findIndex((t) => t.id === taskModal.tarea?.id);
          handleStartTask(taskModal.tarea, "start", idx);
        }}
      />
      <StreakModal open={openStreak} onClose={() => setOpenStreak(false)} totalDays={100} />

      {/* ===== Modal de compra de comodín ===== */}
      {comodin && (
        <BuyComodinModal
          open={confirmBuyOpen}
          item={comodin}
          coinsNow={coinsNow}
          confirming={buyingComodin}
          canBuy={canBuyComodin}
          onCancel={() => setConfirmBuyOpen(false)}
          onConfirm={buyOneComodin}
        />
      )}

      {/* ===== Styles ===== */}
      <style>{`
:root{
  --rad:12px;
  --pad:14px;
  --gap:12px;
  --gap-lg:18px;
  --ring-glow:#a5b4fc;
  --shadow-card:0 10px 28px rgba(0,0,0,.07);
  --border-soft:#e2e8f0;
  --bg-page:#f8fafc;
  --text-main:#0f172a;
  --text-dim:#64748b;
  --brand:#1d0dad;
}

/* ====== Buy Comodín Modal (claro, morado + verde) ====== */
.buy-overlay{
  position:fixed;
  inset:0;
  z-index:9998;
  display:grid;
  place-items:center;
  padding:16px;
  background:rgba(15,23,42,.45);
  backdrop-filter:blur(6px);
  animation: cxmFade .2s ease;
}

.buy-card{
  width:min(92vw, 420px);
  background:#ffffff;
  border-radius:20px;
  border:1px solid #e5e7eb;
  box-shadow:0 24px 60px rgba(15,23,42,.28);
  position:relative;
  padding:42px 18px 16px;
  color:#0f172a;
}

.buy-ribbon{
  position:absolute;
  top:0;
  left:0;
  right:0;
  height:32px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:linear-gradient(90deg,#4f46e5,#7c3aed);
  box-shadow:0 10px 20px rgba(79,70,229,.35);
  border-radius:20px 20px 0 0;
}

.buy-ribbon-pill{
  padding:4px 10px;
  border-radius:9999px;
  border:1px solid rgba(255,255,255,.5);
  color:#f9fafb;
  font-size:.72rem;
  font-weight:800;
  letter-spacing:.08em;
  text-transform:uppercase;
  backdrop-filter:blur(3px);
  background:linear-gradient(120deg,rgba(255,255,255,.28),rgba(255,255,255,.08));
}

.buy-close{
  position:absolute;
  top:6px;
  right:8px;
  width:26px;
  height:26px;
  border-radius:9999px;
  border:none;
  background:rgba(15,23,42,.08);
  color:#0f172a;
  font-weight:900;
  font-size:1rem;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:center;
}

.buy-header{
  display:flex;
  align-items:flex-start;
  gap:10px;
  margin-bottom:12px;
}

.buy-icon-wrap{
  flex:0 0 auto;
  width:52px;
  height:52px;
  border-radius:9999px;
  background:radial-gradient(120px 90px at 30% 0%,rgba(129,140,248,.35),transparent 65%),#f5f3ff;
  border:1px solid #ddd6fe;
  display:flex;
  align-items:center;
  justify-content:center;
}

.buy-icon-circle{
  width:32px;
  height:32px;
  border-radius:9999px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:linear-gradient(135deg,#4f46e5,#7c3aed);
  color:#f9fafb;
  font-weight:800;
  font-size:1rem;
  box-shadow:0 8px 18px rgba(79,70,229,.4);
}

.buy-header-text{
  flex:1 1 auto;
  min-width:0;
}

.buy-title{
  margin:0;
  font-size:1.1rem;
  font-weight:900;
  color:#111827;
}

.buy-sub{
  margin:4px 0 0;
  font-size:.9rem;
  color:#4b5563;
  line-height:1.45;
}

.buy-stats{
  margin-top:12px;
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:8px;
}

.buy-stat{
  padding:8px 10px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#f9fafb;
}

.buy-stat-main{
  background:linear-gradient(135deg,#f5f3ff,#eef2ff);
  border-color:#ddd6fe;
}

.buy-stat-label{
  font-size:.72rem;
  text-transform:uppercase;
  letter-spacing:.06em;
  color:#6b7280;
  margin-bottom:2px;
}

.buy-stat-value{
  font-size:.98rem;
  font-weight:800;
  color:#111827;
}

.buy-warning{
  margin-top:10px;
  padding:8px 10px;
  border-radius:12px;
  background:#fef2f2;
  border:1px solid #fecaca;
  color:#b91c1c;
  font-size:.85rem;
}

.buy-note{
  margin-top:8px;
  padding:8px 10px;
  border-radius:12px;
  background:#f0fdf4;
  border:1px solid #bbf7d0;
  color:#166534;
  font-size:.8rem;
}

.buy-actions{
  margin-top:14px;
  display:flex;
  justify-content:flex-end;
  gap:8px;
  flex-wrap:wrap;
}

/* ✅ Botón desaturado cuando no hay suficientes monedas */
.rt-btn-disabled-bw {
  filter: grayscale(100%);
  opacity: 0.6;
  cursor: not-allowed;
}

.rt-btn-disabled-bw:hover {
  transform: none !important;
  filter: grayscale(100%) !important;
}

.rt-btn{
  position:relative;
  overflow:hidden;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  padding:10px 14px;
  border-radius:10px;
  border:1px solid var(--rt-card-border, #e5e7eb);
  background:#fff;
  color:var(--rt-text-main, #0f172a);
  font-weight:700;
  font-size:.9rem;
  cursor:pointer;
  line-height:1.2;
  transition:transform .12s ease, box-shadow .15s ease, background .15s ease;
}

.rt-btn:hover{
  transform: translateY(-1px);
  box-shadow: 0 10px 30px rgba(0,0,0,.08);
}

.rt-btn-primary{
  background: linear-gradient(90deg,#4f46e5,#7c3aed);
  color:#fff;
  border:none;
  box-shadow: 0 10px 24px rgba(99,102,241,.18);
}

.rt-btn-primary:hover{
  filter:brightness(1.05);
}

.rt-btn-ghost{
  background:#f8fafc;
  border:1px solid #e5e7eb;
  color:#0f172a;
}

@media(max-width:480px){
  .buy-card{
    padding-inline:14px;
  }
  .buy-stats{
    grid-template-columns:1fr;
  }
}

/* ===== RESET MOBILE SAFE ===== */
* { box-sizing:border-box; max-width:100%; }
html, body { width:100%; overflow-x:hidden; }
img, svg { max-width:100%; height:auto; flex-shrink:0; }

/* ===== PAGE WRAPPER ===== */
.task-page{
  min-height:100dvh;
  padding-bottom:calc(var(--footer-h, 120px) + 16px);
  display:flex;
  flex-direction:column;
  gap:16px;
  padding-left:clamp(12px,2vw,20px);
  padding-right:clamp(12px,2vw,20px);
  padding-top:12px;
  max-width:1280px;
  margin:0 auto;
  width:100%;
  overflow-x:hidden;
}

/* helper: ocultar texto en mobile */
@media(min-width:768px){ .hide-xs{ display:inline-block; } }
@media(max-width:767px){ .hide-xs{ display:none; } }

/* ================================
   ✅ TOPBAR (copiada del 1er código)
   ================================ */
.task-topbar{
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(248,250,252,.8);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(226,232,240,.6);
  border-radius: 14px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  box-shadow: 0 8px 24px rgba(0,0,0,.06);
  flex-wrap: nowrap;
  gap: 8px;
  max-width: 100%;
  overflow: hidden;
}

.back-button{
  flex-shrink: 0;
  position:relative;
  overflow:hidden;
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:8px 12px;
  background:#fff;
  border:1px solid var(--border-soft);
  border-radius:10px;
  cursor:pointer;
  color:var(--text-main);
  font-weight:600;
  line-height:1;
  font-size:.9rem;
  box-shadow:0 1px 2px rgba(0,0,0,.04) inset;
  transition: transform .12s ease, background .2s ease, box-shadow .2s ease;
  max-width: 120px;
}
.back-button:hover{
  background:#f1f5f9;
  transform: translateY(-1px);
}

.topbar-center{
  flex:1 1 auto;
  min-width:0;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:.8rem;
  font-weight:600;
  line-height:1.2;
  color:#475569;
  text-align:center;
  flex-wrap:wrap;
  row-gap:2px;
  column-gap:4px;
  word-break: break-word;
  overflow:hidden;
  text-overflow:ellipsis;
}
.topbar-label{
  max-width:140px;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:var(--text-main);
  font-weight:600;
}
.topbar-divider{
  padding:0 4px;
  color:#94a3b8;
}
.topbar-sub{
  max-width:140px;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:#64748b;
  font-weight:500;
}

.topbar-spacer{
  width:44px;
  height:32px;
  flex-shrink:0;
}

.next-topbar-btn{
  flex-shrink:0;
  position:relative;
  overflow:hidden;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  padding:8px 12px;
  border-radius:9999px;
  border:1px solid #0fae0fff;
  background:#ffffffff;
  color:#148b14ff;
  font-weight:700;
  font-size:.8rem;
  line-height:1;
  box-shadow:
    0 12px 28px rgba(17,24,39,.28),
    inset 0 1px 0 rgba(255,255,255,.06),
    0 0 0 0 rgba(59,130,246,.18),
    0 0 22px rgba(59,130,246,.25);
  animation: nextBtnGlow 2.4s ease-in-out infinite;
  transition: transform .12s ease, filter .2s ease;
  max-width: 120px;
  text-wrap: nowrap;
}
.next-topbar-btn:hover{
  transform: translateY(-1px);
  filter: brightness(1.05);
}
@keyframes nextBtnGlow{
  0%,100%{
    box-shadow:
      0 12px 28px rgba(17,24,39,.28),
      inset 0 1px 0 rgba(255,255,255,.06),
      0 0 0 0 rgba(59,130,246,.18),
      0 0 22px rgba(59,130,246,.25);
  }
  50%{
    box-shadow:
      0 12px 28px rgba(17,24,39,.28),
      inset 0 1px 0 rgba(255,255,255,.06),
      0 0 0 6px rgba(59,130,246,.12),
      0 0 32px rgba(59,130,246,.35);
  }
}

@media(min-width:768px){
  .task-topbar{ min-height:56px; padding:12px 16px; }
  .topbar-center{ font-size:.9rem; }
  .topbar-label, .topbar-sub{ max-width:260px; }
}

/* ================================
   ✅ HERO (RESPONSIVO como tu imagen)
   Tarjeta grande: header + ring
   Tarjeta pequeña: gamification
   ================================ */

.hero-wrap{
  display:grid;
  grid-template-columns: 1fr;
  gap:14px;
  max-width:100%;
  overflow-x:hidden;
}

/* Desktop: 2 columnas */
@media(min-width:900px){
  .hero-wrap{
    grid-template-columns: 1fr 320px;
    align-items:stretch;
  }
}

/* ===== CONTENEDOR DE LOS DOS HEADERS ===== */
.hero-container{
  display: grid;
  grid-template-columns: 1fr 240px;
  gap: 12px;
  align-items: stretch;
}
@media (max-width: 900px){
  .hero-container{ grid-template-columns: 1fr; }
}

/* ===== TARJETA IZQUIERDA ===== */
.hero-left-card{
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 16px 18px 14px;
  border-radius: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.05);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.hero-top-row{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.component-title{
  margin: 0;
  font-size: clamp(1.1rem, 2.5vw, 1.5rem);
  font-weight: 900;
  line-height: 1.2;
  color: var(--brand, #1d0dad);
  word-break: break-word;
  flex: 1 1 auto;
}

.hero-controls{
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.ctrl-btn{
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
  font-size: .8rem;
  color: #334155;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
  transition: background .15s, border-color .15s, transform .12s;
  white-space: nowrap;
}
.ctrl-btn:hover{
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}
.ctrl-btn .count-badge{
  font-size: .68rem;
  font-weight: 800;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 1px 6px;
  color: #374151;
}

.hero-progress-wrap{
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.hero-progress-labels{
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.hero-progress-label{
  font-size: .68rem;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.hero-progress-pct{
  font-size: .8rem;
  font-weight: 900;
  transition: color .4s;
}
.hero-progress-track{
  height: 7px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0,0,0,.06);
}
.hero-progress-fill{
  height: 100%;
  border-radius: 999px;
  transition: width .8s cubic-bezier(.4,0,.2,1), background .4s;
}

/* ===== TARJETA DERECHA ===== */
.hero-right-card{
  background: transparent;
  border: none;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  position: relative;
  overflow: visible;
  min-width: 0;
  width: 100%;
}

/* neutralizar clases que ya no se usan */
.hero-left-header{ display: contents; }
.hero-ring{ display: none; }
.resources-btn-hero{ display: none; }

/* Tarjeta pequeña (Paso 3 y 4) */
.hero-stats-card{
  background:#ffffff;
  border:1px solid rgba(226,232,240,.85);
  border-radius:16px;
  box-shadow: var(--shadow-card);
  padding:14px;
  display:flex;
  align-items:center;
  justify-content:center;
  min-width:0;
}


/* ===== GAMIFICATION CARDS tweaks ===== */
.gamification-stats{ display:flex; gap:12px; flex-wrap:wrap; max-width:100%; }
.stat-card{
  position:relative;
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:14px;
  padding:12px 14px;
  min-width:140px;
  flex:1 1 auto;
  display:flex;
  align-items:center;
  gap:10px;
  box-shadow:0 6px 24px rgba(0,0,0,.06);
  max-width:100%;
}
@media(min-width:768px){ .stat-card{ min-width:150px; } }
.stat-btn{ cursor:pointer; transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease, background .12s ease; }
.stat-btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.08); border-color:#cbd5e1; background:#f8fafc; }
.stat-icon{ width:36px; height:36px; border-radius:999px; background:#f1f5ff; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.stat-value{ font-weight:800; color:var(--brand); line-height:1; font-size:1rem; word-break:break-word; }
.stat-label{ color:#6b7280; font-size:.8rem; line-height:1.2; word-break:break-word; }

/* ===== GRID DE TAREAS ===== */
.tasks-wrapper{ display:block; max-width:100%; overflow-x:hidden; }
.tasks-grid{
  display:grid;
  grid-template-columns:1fr;
  gap:16px;
  max-width:100%;
}
@media(min-width:640px){
  .tasks-grid{ grid-template-columns:repeat(2,minmax(0,1fr)); gap:18px; }
}
@media(min-width:1080px){
  .tasks-grid{ grid-template-columns:repeat(3,minmax(0,1fr)); gap:20px; }
}
 
/* ===== CARD DE TAREA — nueva estética ===== */
@keyframes cardIn{
  from{ opacity:0; transform:translateY(10px); }
  to{ opacity:1; transform:translateY(0); }
}
 
.task-card{
  position:relative;
  border-radius:16px;
  padding:18px 18px 16px 22px;   /* izquierda extra para la barra */
  box-shadow:0 1px 4px rgba(0,0,0,.05), 0 4px 16px rgba(0,0,0,.06);
  transition:box-shadow .18s ease, transform .18s ease;
  animation:cardIn .4s both;
  cursor:default;
  max-width:100%;
  overflow:hidden;
  display:flex;
  flex-direction:column;
  gap:10px;
  min-height:170px;
  /* variables de tilt */
  --rx:0deg; --ry:0deg;
}
 
@media(min-width:768px){
  .task-card{
    transform:perspective(700px) rotateX(var(--rx,0)) rotateY(var(--ry,0));
  }
}
 
.task-card:hover{
  box-shadow:0 4px 12px rgba(0,0,0,.07), 0 12px 32px rgba(0,0,0,.09);
}
 
/* Pending */
.task-card.pending{
  background:#fffcf7;
  border:1px solid #fed7aa;
}
/* Done */
.task-card.completed{
  background:#ffffff;
  border:1px solid #bbf7d0;
}
/* Locked */
.task-card.locked{
  background:#f8fafc;
  border:1px solid #cbd5e1;
  opacity:.9;
  cursor:not-allowed;
}
.task-card.locked:hover{
  transform:none;
  box-shadow:0 1px 4px rgba(0,0,0,.05), 0 4px 16px rgba(0,0,0,.06);
}
 
/* ===== BARRA LATERAL ===== */
.task-side-bar{
  position:absolute;
  top:0; left:0;
  width:5px;
  height:100%;
  border-radius:16px 0 0 16px;
}
.bar-pending{ background:#f97316; }
.bar-done{ background:#16a34a; }
.bar-locked{ background:#94a3b8; }
 
/* ===== FILA SUPERIOR ===== */
.task-header-top{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
  flex-wrap:wrap;
}
 
/* ===== PILL DE ESTADO ===== */
.task-status-pill{
  display:inline-flex;
  align-items:center;
  gap:5px;
  border-radius:9999px;
  padding:3px 10px;
  font-size:.72rem;
  font-weight:700;
  letter-spacing:.04em;
  text-transform:uppercase;
  white-space:nowrap;
}
.status-pending{
  background:#f97316;
  color:#fff7ed;
  box-shadow:0 3px 8px rgba(249,115,22,.3);
}
.status-done{
  background:#16a34a;
  color:#ecfdf5;
  box-shadow:0 3px 8px rgba(22,163,74,.3);
}
.status-locked{
  background:#64748b;
  color:#f1f5f9;
  box-shadow:none;
}
 
/* ===== FECHA / COMPLETION INFO ===== */
.completion-info{
  display:flex;
  align-items:center;
  gap:5px;
  color:#94a3b8;
  font-size:.75rem;
  font-weight:600;
}
 
/* ===== TÍTULO ===== */
.task-title{
  margin:0;
  font-size:.95rem;
  font-weight:800;
  color:#1e293b;
  line-height:1.3;
  word-break:break-word;
  flex:1;
}
.task-card.locked .task-title{ color:#64748b; }
 
/* ===== CHIPS UNIFICADOS (recuadro horizontal) ===== */
.task-chips-row{
  display:flex;
  align-items:center;
  background:rgba(241,245,249,.8);
  border:1px solid #e2e8f0;
  border-radius:10px;
  overflow:hidden;
  flex-shrink:0;
}
.task-chip-item{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  flex:1;
  padding:7px 8px;
  font-size:.7rem;
  font-weight:700;
  color:#475569;
  white-space:nowrap;
}
.chip-icon-q{ color:#3b82f6; flex-shrink:0; }
.chip-icon-g{ color:#7c3aed; flex-shrink:0; }
.task-chip-divider{
  width:1px;
  height:24px;
  background:#e2e8f0;
  flex-shrink:0;
}
.task-card.locked .task-chips-row{ opacity:.7; }
 
/* ===== ACCIONES ===== */
.task-actions{
  display:flex;
  gap:8px;
  margin-top:auto;
  padding-top:4px;
  flex-wrap:nowrap;
  width:100%;
  justify-content:center;
}
.task-actions .start-wrap{ flex:1 1 auto; display:flex; justify-content:center; }
.task-actions .start-wrap button,
.task-actions .start-wrap > *{ width:100% !important; }
.task-actions .btn-ghost{ flex:1 1 auto; }
 
/* Botón ghost (Detalles) */
.btn{
  position:relative;
  overflow:hidden;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
  gap:6px;
  padding:9px 16px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#fff;
  color:#334155;
  font-weight:700;
  font-size:.8rem;
  line-height:1.2;
  cursor:pointer;
  transition:background .15s ease, border-color .15s ease, transform .12s ease, box-shadow .15s ease;
  max-width:100%;
  text-align:center;
  word-break:break-word;
  white-space:nowrap;
  box-shadow:0 1px 3px rgba(0,0,0,.06);
}
.btn:hover{
  background:#f8fafc;
  border-color:#cbd5e1;
  transform:translateY(-1px);
  box-shadow:0 4px 12px rgba(0,0,0,.08);
}
.btn:active{ transform:translateY(0); }
.btn-ghost{ background:#ffffff; }
 
.btn.btn-locked{
  opacity:.7;
  filter:grayscale(.3);
  border-color:#cbd5e1;
  background:#f8fafc;
  color:#94a3b8;
  cursor:not-allowed;
}
.btn.btn-locked:hover{
  transform:none;
  box-shadow:0 1px 3px rgba(0,0,0,.06);
}
 
/* start-wrap locked */
.start-wrap.is-locked{
  opacity:.8;
  filter:grayscale(.35);
  pointer-events:none;
  user-select:none;
}
.start-wrap.is-locked button{
  cursor:not-allowed !important;
  box-shadow:none !important;
}
 
/* ripple */
.ripple{
  position:absolute;
  border-radius:50%;
  transform:scale(0);
  background:rgba(255,255,255,.55);
  animation:ripple .6s ease-out;
  pointer-events:none;
}
@keyframes ripple{ to{ transform:scale(3); opacity:0; } }
 
.no-tasks{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:8px;
  padding:2rem 1rem;
  color:#6b7280;
}

/* ================================
   ✅ LOCKED (gris elegante y claro)
   ================================ */

/* Card locked: gris limpio, sin "shine", sin hover levantado */
.task-card.locked{
  opacity: 1;
  filter: none;
  cursor: not-allowed;
  border-color: #cbd5e1;
  background:
    radial-gradient(900px 220px at 20% -40%, rgba(148,163,184,.35), transparent 55%),
    linear-gradient(135deg, #f8fafc 0%, #eef2f7 55%, #e8edf5 100%);
  box-shadow:
    0 8px 24px rgba(15,23,42,.08),
    inset 0 1px 0 rgba(255,255,255,.7);
  transform: none !important;
}

/* Desactiva el brillo animado en bloqueadas */
.task-card.locked.shine::before{
  opacity: 0 !important;
  animation: none !important;
}

/* Overlay sutil (pattern diagonal) */
.task-card.locked::after{
  content:"";
  position:absolute;
  inset:0;
  pointer-events:none;
  background:
    linear-gradient(135deg,
      rgba(148,163,184,.14) 0%,
      rgba(148,163,184,.05) 18%,
      rgba(255,255,255,0) 45%),
    repeating-linear-gradient(
      135deg,
      rgba(148,163,184,.10) 0px,
      rgba(148,163,184,.10) 8px,
      rgba(255,255,255,0) 8px,
      rgba(255,255,255,0) 16px
    );
  opacity:.55;
}

/* Icon y textos más “apagados” */
.task-card.locked .task-title{
  color:#334155;
  opacity:.95;
}
.task-card.locked .task-chips,
.task-card.locked .task-status-pill,
.task-card.locked .completion-info,
.task-card.locked .task-progress-row{
  opacity:.88;
}

/* Icon container en locked */
.task-icon.locked-ic{
  background: linear-gradient(135deg,#f1f5f9 0%, #e2e8f0 100%);
  border-color:#cbd5e1;
  color:#64748b;
}

/* Pill de estado cuando está locked */
.task-status-pill.locked-pill{
  background: rgba(148,163,184,.22) !important;
  color:#334155 !important;
  box-shadow:none !important;
  border: 1px solid rgba(148,163,184,.35);
}

/* Dot / texto de progreso en locked */
.task-progress-dot.dot-locked{
  background:#94a3b8 !important;
}
.task-progress-text.text-locked{
  color:#475569 !important;
  font-weight:700;
}

/* Badge de bloqueo (arriba derecha) */
.task-lock-badge{
  position:absolute;
  top:10px;
  right:10px;
  z-index:3;
  display:flex;
  align-items:center;
  gap:8px;
  padding:8px 10px;
  border-radius:999px;
  background: rgba(15,23,42,.06);
  border:1px solid rgba(148,163,184,.55);
  color:#0f172a;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 10px 22px rgba(15,23,42,.10);
}

.task-lock-badge .lock-dot{
  width:22px;
  height:22px;
  display:grid;
  place-items:center;
  border-radius:999px;
  background: rgba(148,163,184,.18);
  border:1px solid rgba(148,163,184,.45);
  font-size:.9rem;
}

.task-lock-badge .lock-text{
  font-weight:950;
  font-size:.78rem;
  letter-spacing:.02em;
  text-transform:uppercase;
  color:#0f172a;
  line-height:1;
}

.task-lock-badge .lock-sub{
  font-weight:800;
  font-size:.76rem;
  color:#475569;
  line-height:1;
  opacity:.95;
}

/* Start button wrapper (solo visual) */
.start-wrap.is-locked{
  opacity:.85;
  filter: grayscale(.35);
}

.start-wrap.is-locked{
  pointer-events: none;   /* ✅ no mouse/touch */
  user-select: none;
}

/* Si el StartActivityButton renderiza un <button>, lo “apagamos” sin romper nada */
.start-wrap.is-locked button{
  cursor: not-allowed !important;
  opacity: .92;
  box-shadow: none !important;
}

/* Detalles también gris cuando locked */
.btn.btn-locked{
  opacity:.9;
  filter: grayscale(.35);
  border-color:#cbd5e1;
  background:#f8fafc;
  color:#334155;
}
.btn.btn-locked:hover{
  transform:none;
  box-shadow:none;
}

/* Evita hover “lift” en toda la card locked */
.task-card.locked:hover{
  transform:none;
  box-shadow:
    0 8px 24px rgba(15,23,42,.08),
    inset 0 1px 0 rgba(255,255,255,.7);
}

/* ===== TOUR / TUTORIAL ===== */
.tour-root{
  position: fixed;
  inset: 0;
  z-index: 99999;
  pointer-events: none;
}
.tour-card{
  pointer-events: auto;
}
.tour-actions,
.tour-btn{
  pointer-events: auto;
}
.tour-dim{
  position: fixed;
  background: rgba(2, 6, 23, .35);
  backdrop-filter: blur(4px) saturate(1.05);
  -webkit-backdrop-filter: blur(4px) saturate(1.05);
  animation: tourFade .18s ease-out both;
  pointer-events: auto;
}
@keyframes tourFade{
  from{ opacity: 0; }
  to{ opacity: 1; }
}
.tour-hole{
  position: fixed;
  z-index: 100000;
  border: 1px solid rgba(255,255,255,.30);
  box-shadow:
    0 0 0 9999px rgba(2, 6, 23, .22),
    0 0 0 6px rgba(255,255,255,.08),
    0 22px 70px rgba(0,0,0,.45);
  animation: holePulse 1.2s ease-in-out infinite;
  transition: top .22s ease, left .22s ease, width .22s ease, height .22s ease,
    border-color .22s ease, border-radius .22s ease, box-shadow .22s ease;
  pointer-events: none;
}
.tour-hole.clickable{
  pointer-events: auto;
  cursor: pointer;
}
@keyframes holePulse{
  0%,100%{
    border-color: rgba(255,255,255,.28);
    box-shadow:
      0 0 0 9999px rgba(2, 6, 23, .35),
      0 0 0 6px rgba(255,255,255,.08),
      0 22px 70px rgba(0,0,0,.45);
  }
  50%{
    border-color: rgba(255,255,255,.55);
    box-shadow:
      0 0 0 9999px rgba(2, 6, 23, .35),
      0 0 0 10px rgba(255,255,255,.12),
      0 22px 70px rgba(0,0,0,.45);
  }
}
.tour-card{
  position: fixed;
  width: min(560px, calc(100vw - 24px));
  background: #ffffff;
  opacity: 1;
  border: 1px solid rgba(226,232,240,.98);
  border-radius: 18px;
  box-shadow: 0 34px 120px rgba(0,0,0,.45);
  z-index: 100002;
  padding: 14px 14px 12px;
  pointer-events: auto;
}
.tour-card:not(.has-target){
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
.tour-card.has-target{
  transform: none;
}
.tour-card-centered{
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
}
.tour-card-comodines{
  left: auto !important;
  right: 16px !important;
}
.tour-card-left-bottom{
  left: 16px !important;
}
.tour-card-final-start{
  left: 16px !important;
}
@keyframes tourPop{
  from{ transform: translateY(8px) scale(.99); opacity: 0; }
  to{ transform: translateY(0) scale(1); opacity: 1; }
}
.tour-head{
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}
.tour-title{
  color: #0f172a;
  font-weight: 950;
  letter-spacing: .2px;
  font-size: 1.02rem;
}
.tour-step{
  font-weight: 900;
  font-size: .78rem;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  flex: 0 0 auto;
}
.tour-text{
  margin-top: 8px;
  color: #334155;
  line-height: 1.55;
  font-size: .95rem;
}
.tour-p{ margin: 0 0 8px 0; }
.tour-p:last-child{ margin-bottom: 0; }
.tour-warning{
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-weight: 900;
}
.tour-actions{
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.tour-btn{
  border: none;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 950;
  cursor: pointer;
  transition: transform .12s ease, filter .12s ease, background .12s ease;
}
.tour-btn.primary{
  background: linear-gradient(135deg,#2563eb,#1d4ed8);
  color: #fff;
  border: 1px solid #1d4ed8;
  box-shadow: 0 14px 34px rgba(37,99,235,.22), inset 0 1px 0 rgba(255,255,255,.35);
}
.tour-btn.primary:hover{ transform: translateY(-1px); filter: brightness(1.05); }
.tour-btn.ghost{
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #0f172a;
}
.tour-btn.ghost:hover{ background: #f8fafc; transform: translateY(-1px); }

/* ===== COMPLETION MODAL ===== */
.completion-modal-backdrop{
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99998;
  padding: 20px;
}
.completion-modal-card{
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 60px rgba(0,0,0,.18);
  overflow: hidden;
  animation: modalIn .2s ease-out;
}
.completion-modal-head{
  background: linear-gradient(135deg,#16a34a,#15803d);
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #fff;
}
.completion-modal-head h3{
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: #fff;
}
.completion-icon{
  color: #fff;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,.2));
}
.completion-modal-body{
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #374151;
  font-size: .9rem;
  line-height: 1.6;
}
.completion-modal-body p{ margin: 0; }
.completion-help-hint{
  font-size: .82rem;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
}
.completion-modal-actions{
  padding: 0 20px 20px;
  display: flex;
  justify-content: center;
}

/* ===== TOUR LIVES DEMO ===== */
.tour-lives-demo{
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.lives-display{
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.q-heart{
  font-size: 1.1rem;
  line-height: 1;
}


      `}</style>
    </div>
  );
};

export default ComponenteTareas;
